/**
 * Oversikt (Masjid UX redesign, 2026-10-23): what needs me, what is next, and — folded — the numbers the board asks
 * for. Every card says WHY it matters in one sentence and has one button; a count is always inside a sentence, never
 * alone (ADM-06). Every card is built from an endpoint the panel already uses, with that endpoint's own permission, so a
 * person sees only cards about work they may do. Nothing urgent: one calm line.
 *
 * Before the redesign: Dashbord.  CA-31, from core dashboard.js via GET /admin/dashboard.
 *
 * - The tile labelled "Medlemmer" shows the number of members, not the age
 *   of the import (the same class of lie as a UUID labelled as a name).
 * - The screens tile counts total − offline (the card has no `online`) and
 *   NAMES the dark screen, which is the whole point of the card.
 * - "Krever handling" lists exactly the cards that carry an action: a card
 *   reporting that something is fine does not belong under that heading.
 *   Since 2026-10-15 that includes membership work waiting for THIS person
 *   (core dashboard.membershipWork, counted per viewer by the server): its
 *   «Åpne» goes to Medlemmer › Til behandling.
 *
 * 2026-09-20 (Daniyal): the page was four tiles and a table, and then a
 * screenful of nothing. Two things went into that space, and neither is new
 * data — both were already being computed and thrown away:
 *
 *   1. **`facts`.** core's `dashboard.facts()` has produced the numbers the
 *      board is asked in meetings — members, how many have linked the app,
 *      what happened this month — since CA-41, the API has returned them all
 *      along, and this file rendered `cards` and dropped `facts` on the
 *      floor. (Three of those counts were also zero at the server; see the
 *      dashboard route.)
 *   2. **The all-clear.** `allClear_no` exists so that a green dashboard says
 *      "Alt ser bra ut" once, instead of showing five green cards that train
 *      people to stop reading them. It had no renderer either.
 *
 * Owner 2026-10-23 («you are not showing all stuff in oversikt, for example if someone ordered something»): paid shop
 * orders waiting to be handed over, the person's own open and overdue tasks, checklists due, sign-up lists with a waiting
 * list or an event this week — each from the endpoint its own page uses, asked only by someone who may open that page —
 * and, folded, «Siste døgn»: what came in during the last 24 hours (gifts, orders, requests), as sentences. Applications are not
 * counted here: the list they come from carries each applicant's details and register matching — the card above says how
 * many wait (review 2026-10-23).
 *
 * «Utvikling» (meeting notes 2026-10-31): gifts per month, members in and out, people coming per event — bars per
 * period from the server's `charts` (core dashboard.charts), each chart only for someone who may read it
 * (components/DashboardCharts.tsx); and «Denne måneden: X kr» beside «Siste døgn», from the same numbers.
 *
 * What is still NOT here, and must not arrive: a trend line, a target, a
 * percentage of anything month over month. dashboard.js states the test —
 * would the board do something differently if this number changed? — and a
 * chart of app sign-ins is how a mosque ends up sending push notifications
 * about nothing.
 */
import React, { useState } from 'react';
import { can } from '../lib/api';
import { NotifyPrefsDialog, useMailNudge } from '../components/NotifyPrefs';
import { useLoad } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { bannersToApprove } from '../lib/refresh';
import { kr, osloDay } from '../lib/format';
import { handoverDue } from '../lib/store-orders';
import { Card, Loading, LoadError, More } from '../components/ui';
import { WorkCard, useWorkHub } from '../components/WorkCard';
import { SetupCard } from '../components/SetupCard';
import { DashboardCharts, type DashCharts } from '../components/DashboardCharts';
import { CAPTURABLE } from './Donations';
import { Shell } from '../core';

type DashCard = { id: string; severity: string; title_no?: string; action_no?: string | null; [k: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
type B = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
interface Facts {
  members: number | null; membersActive: number | null; appUsersLinked: number | null;
  thisMonth: { events: number; requests: number; pushesSent: number; janazahAnnounced: number };
  period_no: string;
}
/** Where a core dashboard card's «Åpne» goes (core dashboard.js ids → panel views). */
const VIEW_FOR: Record<string, string> = { timetable: 'times', screens: 'screens', inbox: 'inbox', translation: 'trans', import: 'members', membership: 'members' };

/** «1 søknad» / «3 søknader». */
const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
/** A gift that brought money (reserved, being drawn, drawn): what «Siste døgn» counts — not a Vipps window left open. */
const GOT = ['authorized', 'capture_pending', 'capture_failed', 'captured'];
/** Today in Oslo as YYYY-MM-DD (checklist due days are mosque days). */
const osloToday = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' });

/** One thing that needs attention: a title (with the count in words), why it matters, one button. */
function Attention({ tone, title, why, action, onGo }: { tone: 'urgent' | 'waiting' | 'info'; title: string; why: string; action: string; onGo?: (() => void) | null }) {
  return (
    <li className={'att ' + tone}>
      <div className="att-text"><b>{title}</b><span>{why}</span></div>
      {/* No button to a page this person cannot open (review F8): the card still says what is wrong. */}
      {onGo && <button type="button" className="btn sm att-go" onClick={onGo}>{action}</button>}
    </li>
  );
}

/** A plain count for the board. No severity: a number of members is not good or bad news. */
function Fact({ k, v, d }: { k: string; v: number | null; d?: string }) {
  return (
    <div className="tile quiet"><div className="k">{k}</div>
      {/* "—", never 0, when the server could not answer: a mosque told it has 0 members has been told something false. */}
      <div className="v">{v == null ? '—' : v.toLocaleString('nb-NO')}</div>
      <div className="d">{d || ''}</div></div>
  );
}

/**
 * The start page for an account WITHOUT operations.read — a volunteer (Batch 1 hardening, 2026-10-02). Their own work and
 * nothing else: no member, request, screen or app-user counts, and no request for them (/dashboard would refuse anyway).
 */
function MyWorkHome() {
  const work = useWorkHub();
  if (work) return <WorkCard />;
  return (
    <Card title="Mitt arbeid">
      <div className="card-body">
        <p>Her ser du oppgavene og sjekkpunktene dine når moskeen bruker oppgaver eller sjekklister.</p>
        <p className="note">Kontoen din har ikke tilgang til resten av oversikten. Spør en administrator hvis du trenger mer.</p>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const { go, caps } = useNav();
  const overview = can('operations.read');
  // E-postvarsler (owner 2026-10-23): asked once here, until the person has chosen anything; nothing is sent before that.
  const nudge = useMailNudge();
  const [mailOpen, setMailOpen] = useState(false);
  // Closing the popup is an answer for now: not asked again until the next sign-in (the panel keeps nothing in localStorage).
  const NUDGE_KEY = 'masjid.mail-nudge.dismissed';
  const [nudgeGone, setNudgeGone] = useState(() => { try { return sessionStorage.getItem(NUDGE_KEY) === '1'; } catch { return false; } });
  const { data, error, reload } = useLoad<{ cards: DashCard[]; facts?: Facts; allClear_no?: string | null; charts?: DashCharts }>(overview ? '/dashboard' : null);
  // The work queues — each asked only by someone who may act on it (the same permissions as the menu badges).
  const work = useLoad<B>(overview && can('members.link') ? '/membership-work' : null);
  const notices = useLoad<B>(overview && (can('notices.approve') || can('push.approve')) ? '/notices' : null);
  const banners = useLoad<B>(overview && caps && caps.includes('banners') && can('banners.approve') ? '/banners' : null);
  // Gifts: read for «Siste døgn»; the capture card below still needs donations.capture.
  // Gifts: only with capture too, as before — GET /donations writes an audit row, so it is not asked on every view by every reader.
  const gifts = useLoad<B>(overview && can('donations.read') && can('donations.capture') ? '/donations' : null);
  const events = useLoad<B>(overview && can('events.write') ? '/events' : null);
  /** Whether this person may open page `id` (its permission and the mosque's capability) — then and only then is its endpoint asked. */
  const mayOpen = (id: string) => { const item = Shell.NAV.find((n) => n.id === id); return Boolean(item && Shell.permits(item, can) && Shell.capabilityAllows(item, caps)); };
  // Owner 2026-10-23: the shop, the person's own work, sign-up lists, and what came in during the last day.
  const orders = useLoad<B>(overview && mayOpen('store') ? '/store/orders' : null);
  const mywork = useLoad<B>(overview && mayOpen('work') && can('work.assigned') ? '/work/summary' : null);
  const runs = useLoad<B>(overview && mayOpen('checklists') ? (can('checklists.manage') ? '/work/checklists/runs?status=open&limit=200' : '/work/checklists/runs?mine=1&limit=200') : null);
  const signups = useLoad<B>(overview && mayOpen('registration') ? '/event-ops/events?when=upcoming&list=yes&limit=200' : null);
  const requests = useLoad<B>(overview && can('requests.read') ? '/requests' : null);
  // Arrangementer pro (2026-10-31): the årshjul's events this month that have no event yet — only for whoever opens both.
  const ym = osloToday().slice(0, 7);
  const monthEnd = `${ym}-${String(new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0)).getUTCDate()).padStart(2, '0')}`;
  const plan = useLoad<B>(overview && mayOpen('planner') && can('events.write') ? `/work/planner?from=${ym}-01&to=${monthEnd}` : null);
  if (!overview) return <MyWorkHome />;
  /** A button to `id` only when this person may open that page (permission and the mosque's capability). */
  const goTo = (id: string) => (mayOpen(id) ? () => go(id) : null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const cards = data.cards || [];
  const by = (id: string): DashCard => cards.find((c) => c.id === id) || ({ id, severity: 'ok' } as DashCard);
  const items: React.ReactNode[] = [];

  // 1. Members waiting on staff: applications first, they are people waiting for an answer.
  const open = (work.data && work.data.open) || {};
  if (open.applications > 0) items.push(<Attention key="apps" tone="waiting" title={n(open.applications, 'søknad om medlemskap venter på deg', 'søknader om medlemskap venter på deg')}
    why="Søkerne venter på et svar. Åpne en søknad, sjekk hvem det er, og godkjenn eller avslå." action="Gå til søknadene" onGo={goTo('members')} />);
  const otherWork = (open.joinings || 0) + (open.withdrawals || 0) + (open.reconciliations || 0) + (open.confirmations || 0);
  if (otherWork > 0) {
    const parts = [open.joinings && n(open.joinings, 'innmelding', 'innmeldinger'), open.withdrawals && n(open.withdrawals, 'utmelding', 'utmeldinger'),
      open.reconciliations && n(open.reconciliations, 'sak å avklare', 'saker å avklare'), open.confirmations && n(open.confirmations, 'medlemsbevis som feilet', 'medlemsbevis som feilet')].filter(Boolean);
    items.push(<Attention key="mwork" tone="waiting" title={'Medlemskap: ' + parts.join(', ')}
      why="Registrerte innmeldinger og utmeldinger må bekreftes, og avvik fra registeret må avklares." action="Se sakene" onGo={goTo('members')} />);
  }
  // 2. Janazah or an announcement waiting for a second person: the one place minutes matter.
  const waitingNotices = (notices.data && notices.data.awaitingApproval) || 0;
  if (waitingNotices > 0) items.push(<Attention key="notices" tone="urgent" title={n(waitingNotices, 'varsel venter på godkjenning', 'varsler venter på godkjenning')}
    why="En janazah eller kunngjøring vises ikke før en annen ansatt har godkjent den." action="Se og godkjenn" onGo={goTo('notices')} />);
  const waitingBanners = banners.data ? bannersToApprove(banners.data.items) : 0;
  if (waitingBanners > 0) items.push(<Attention key="banners" tone="waiting" title={n(waitingBanners, 'banner venter på godkjenning', 'bannere venter på godkjenning')}
    why="Bannere vises på skjermene og i appen først når de er godkjent." action="Se bannerne" onGo={goTo('banners')} />);
  // 3. Requests from members (nikah, janazah, visits …).
  const inb = by('inbox');
  if (can('requests.read') && inb.open > 0) items.push(<Attention key="inbox" tone={inb.severity === 'critical' ? 'urgent' : 'waiting'}
    title={n(inb.open, 'henvendelse fra medlemmer er åpen', 'henvendelser fra medlemmer er åpne')} why={inb.action_no || inb.title_no || 'Noen venter på svar fra moskeen.'}
    action="Åpne henvendelsene" onGo={goTo('inbox')} />);
  // 3b. The person's own tasks (the work hub's summary, counted by the server for THIS person).
  const wc: B = (mywork.data && mywork.data.counts) || {};
  if (wc.myOpen > 0) items.push(<Attention key="tasks" tone={wc.myOverdue > 0 ? 'urgent' : 'info'}
    title={n(wc.myOpen, 'oppgave hos deg er ikke ferdig', 'oppgaver hos deg er ikke ferdige')}
    why={wc.myOverdue > 0 ? `${wc.myOverdue} av dem har passert fristen.`
      : wc.myToday > 0 ? `${wc.myToday === 1 ? 'Én har' : wc.myToday + ' har'} frist i dag.` : 'Ingen har passert fristen. Merk dem ferdige når de er gjort.'}
    action="Se oppgavene" onGo={goTo('work')} />);
  // 3c. Checklists that are due today or overdue and not finished.
  const today = osloToday();
  const due: B[] = runs.data ? (runs.data.items || []).filter((r: B) => r.status !== 'done' && r.status !== 'cancelled' && r.dueOn && r.dueOn <= today) : [];
  if (due.length > 0) {
    const over = due.filter((r) => r.dueOn < today).length;
    items.push(<Attention key="checklists" tone={over ? 'urgent' : 'waiting'} title={n(due.length, 'sjekkliste skal være ferdig i dag eller før', 'sjekklister skal være ferdige i dag eller før')}
      why={over ? `${over === 1 ? 'Én har' : over + ' har'} passert datoen. Kryss av det som er gjort, eller flytt datoen.` : 'Kryss av punktene etter hvert som de blir gjort.'}
      action="Se sjekklistene" onGo={goTo('checklists')} />);
  }
  // 3d. Sign-up lists: people on a waiting list, or an event with a list in the coming seven days.
  const lists: B[] = signups.data ? (signups.data.items || []).filter((x: B) => x.list && x.summary && x.event && x.event.status !== 'cancelled') : [];
  const waiting = lists.filter((x) => x.summary.waitlistedEntries > 0);
  const weekAhead = Date.now() + 7 * 86_400_000;
  const soon = lists.filter((x) => { const t = new Date(x.event.startsAt).getTime(); return Number.isFinite(t) && t <= weekAhead; });
  if (waiting.length > 0 || soon.length > 0) {
    const people = waiting.reduce((a, x) => a + (x.summary.waitlistedEntries || 0), 0);
    items.push(<Attention key="signups" tone={waiting.length ? 'waiting' : 'info'}
      title={waiting.length ? n(waiting.length, 'påmeldingsliste har folk på venteliste', 'påmeldingslister har folk på venteliste')
        : n(soon.length, 'arrangement med påmelding er de neste sju dagene', 'arrangementer med påmelding er de neste sju dagene')}
      why={waiting.length ? `${n(people, 'person venter', 'personer venter')} på plass. Rykk opp folk hvis noen melder seg av, eller øk antallet plasser.`
        : 'Se over listen og gjør innsjekkingen klar.'}
      action="Se påmelding" onGo={goTo('registration')} />);
  }
  // 3e. The årshjul's events this month without an event (Arrangementer pro): «Lag utkast til arrangement» is on each.
  const unplanned = plan.data ? (plan.data.items || []).filter((i: B) => i.category === 'event' && i.status === 'active' && !i.eventId && !i.event).length : 0;
  if (unplanned > 0) items.push(<Attention key="planner-events" tone="info" title={`Fra årshjulet denne måneden: ${unplanned} uten arrangement`}
    why="Punkter i årshjulet som er arrangementer, men som ikke har et arrangement i appen ennå. Åpne punktet og velg «Lag utkast til arrangement»."
    action="Se årshjulet" onGo={goTo('planner')} />);
  // 4. Gifts reserved but not captured.
  const toCapture = gifts.data && can('donations.capture') ? (gifts.data.items || []).filter((d: B) => CAPTURABLE.includes(d.payStatus)).length : 0;
  if (toCapture > 0) items.push(<Attention key="gifts" tone="waiting" title={n(toCapture, 'gave er reservert, men ikke trukket', 'gaver er reservert, men ikke trukket')}
    why="Pengene er reservert hos giveren. De må trekkes før reservasjonen går ut." action="Se gavene" onGo={goTo('donations')} />);
  // 4b. Shop orders that are paid (reserved) and waiting to be handed over — the money is drawn at the handover.
  const now = new Date();
  const paid: B[] = orders.data ? (orders.data.items || []).filter((o: B) => o.status === 'paid') : [];
  if (paid.length > 0) {
    const late = paid.filter((o) => handoverDue(o as { status: string; createdAt: string }, now)).length;
    items.push(<Attention key="orders" tone={late ? 'urgent' : 'waiting'} title={n(paid.length, 'betalt bestilling venter på å bli levert', 'betalte bestillinger venter på å bli levert')}
      why={late ? `${late} av dem er fire dager eller eldre: lever eller avbryt, ellers frigis reservasjonen etter seks dager.`
        : 'Kunden har betalt med reservasjon i Vipps. Pengene trekkes når du markerer bestillingen som levert.'}
      action="Se ordrene" onGo={goTo('store')} />);
  }
  // 5. The building: prayer times running out, screens down, translations behind.
  const tt = by('timetable');
  if (tt.action_no) items.push(<Attention key="tt" tone={tt.severity === 'critical' ? 'urgent' : 'info'}
    title={tt.daysRemaining != null ? `Bønnetidene varer i ${tt.daysRemaining} dager til` : (tt.title_no || 'Bønnetidene må oppdateres')} why={tt.action_no}
    action="Gå til bønnetidene" onGo={goTo('times')} />);
  const scr = by('screens');
  if (scr.offline > 0) items.push(<Attention key="scr" tone={scr.severity === 'critical' ? 'urgent' : 'waiting'}
    title={n(scr.offline, 'skjerm i bygget er nede', 'skjermer i bygget er nede')} why={(scr.offlineNames || []).length ? (scr.offlineNames.join(', ') + ' viser ikke noe nå.') : 'De viser ikke bønnetider eller kunngjøringer nå.'}
    action="Se skjermene" onGo={goTo('screens')} />);
  for (const id of ['translation', 'import']) {
    const k = by(id);
    if (k.action_no && VIEW_FOR[id]) items.push(<Attention key={id} tone="info" title={k.title_no || ''} why={k.action_no} action="Åpne" onGo={goTo(VIEW_FOR[id])} />);
  }

  // Review F7: a queue that could not be asked is said, never counted as «nothing waiting».
  const queues = [work, notices, banners, gifts, orders, mywork, runs, signups, requests, plan];
  const failedQueues = queues.filter((x) => x.error).length;
  if (failedQueues) items.push(<Attention key="failed" tone="info" title="Kunne ikke hente alt"
    why="Noe av oversikten svarte ikke. Det kan vente saker som ikke vises her nå." action="Prøv igjen"
    onGo={() => { for (const x of [...queues, events]) if (x.error) void x.reload(); }} />);
  const upcoming: B[] = events.data ? (events.data.items || [])
    .filter((e: B) => e.status === 'published' && e.starts_at && new Date(e.starts_at).getTime() > Date.now())
    .sort((a: B, b: B) => String(a.starts_at).localeCompare(String(b.starts_at))).slice(0, 3) : [];
  // «Siste døgn» (owner 2026-10-23): what came in during the last 24 hours — only what this person may see; no zero lines.
  const since = Date.now() - 86_400_000;
  const fresh = (iso: unknown) => { const t = new Date(String(iso || '')).getTime(); return Number.isFinite(t) && t >= since; };
  const day: string[] = [];
  if (gifts.data) {
    const g = (gifts.data.items || []).filter((d: B) => GOT.includes(d.payStatus) && fresh(d.startedAt));
    if (g.length) day.push(`${n(g.length, 'ny gave', 'nye gaver')} på til sammen ${kr(g.reduce((a: number, d: B) => a + (Number(d.amount) || 0), 0))}.`);
  }
  if (orders.data) {
    const o = (orders.data.items || []).filter((x: B) => x.status !== 'draft' && fresh(x.createdAt));
    if (o.length) day.push(`${n(o.length, 'ny bestilling', 'nye bestillinger')} i butikken.`);
  }
  if (requests.data) {
    const r = [...(requests.data.queue || []), ...(Array.isArray(requests.data.scheduled) ? requests.data.scheduled : [])]
      .filter((x: B, i: number, all: B[]) => all.findIndex((y) => y.id === x.id) === i && fresh(x.created_at || x.createdAt));
    if (r.length) day.push(`${n(r.length, 'ny henvendelse', 'nye henvendelser')} fra medlemmer.`);
  }
  // «Denne måneden» (2026-10-31): what came in this month, from the same numbers as the gift chart (donations.read).
  const month = data.charts && data.charts.gifts ? data.charts.gifts.thisMonth : null;
  const dayShown = Boolean(gifts.data || orders.data || requests.data || month);
  // Snarveier (developer feedback, 2026-10-30): what staff open most, one click from the start page — each only for
  // someone who may open that page. A fixed list for now; one that learns from use was judged not worth it yet.
  const quick = ([['notices', 'Varsler'], ['times', 'Bønnetider'], ['events', 'Arrangementer'], ['banners', 'Bannere'], ['inbox', 'Henvendelser']] as const)
    .map(([id, label]) => { const open = goTo(id); return open ? <button key={id} type="button" className="btn ghost sm" onClick={open}>{label}</button> : null; })
    .filter(Boolean);
  const f = data.facts;
  return (
    <>
      <section aria-labelledby="att-h" className="att-wrap">
        <h2 id="att-h" className="ov-h">Trenger deg nå</h2>
        {items.length ? <ul className="att-list">{items}</ul>
          : <p className="ov-calm" role="status">{data.allClear_no || 'Alt er i orden. Ingenting venter på deg akkurat nå.'}</p>}
      </section>
      {/* PM gaps B4 (2026-11-01): what is missing for the mosque to use what it has — admin tier, absent when nothing is. */}
      <SetupCard />
      {quick.length > 0 && (
        <nav aria-labelledby="quick-h" className="ov-quick">
          <h2 id="quick-h" className="ov-h">Snarveier</h2>
          <div className="ov-quick-row">{quick}</div>
        </nav>
      )}
      {nudge.show && !nudgeGone && (
        <ul className="att-list ov-nudge" aria-label="E-postvarsler">
          <Attention tone="info" title="Vil du få e-post når noe venter?"
            why="En e-post med en gang, eller én oppsummering kl. 07 — om det du har tilgang til. Ingenting sendes før du slår det på."
            action="Velg e-postvarsler" onGo={() => setMailOpen(true)} />
        </ul>
      )}
      <NotifyPrefsDialog open={mailOpen} onClose={() => { setMailOpen(false); setNudgeGone(true); try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch { /* private window */ } nudge.reload(); }} />
      {dayShown && (
        <Card className="ov-facts ov-day">
          <div className="card-body">
            <More label="Siste døgn">
              {day.length ? <ul className="ov-day-list">{day.map((t) => <li key={t}>{t}</li>)}</ul> : <p>Ingenting nytt siste døgn.</p>}
              {month && (month.amount > 0 || (month.cash || 0) > 0) && <p className="note">Denne måneden: {kr(month.amount)}{(month.cash || 0) > 0 ? ` med Vipps og ${kr(month.cash)} i kontanter` : ''}.</p>}
            </More>
          </div>
        </Card>
      )}
      {/* MKK adoption Batch 1: the person's own tasks and checklist points, when the mosque has the work hub. */}
      <WorkCard />
      {upcoming.length > 0 && (
        <Card title="Kommende arrangementer">
          <ul className="ov-events">{upcoming.map((e) => (
            <li key={e.id}><span className="ov-when">{osloDay(e.starts_at)}</span><span>{e.title_no}</span></li>))}
          </ul>
          <div className="card-body" style={{ paddingTop: 0 }}><button type="button" className="link" onClick={() => go('events')}>Se alle arrangementer ›</button></div>
        </Card>
      )}
      {/* «Utvikling» (2026-10-31): below what needs doing, above «Tall for styret». */}
      <DashboardCharts charts={data.charts} />
      {f && (
        <Card className="ov-facts">
          <div className="card-body">
            <More label="Tall for styret">
              <p>Tall styret blir spurt om i møter. «Har koblet appen» er medlemmer som har logget inn og funnet medlemskapet sitt — ikke nedlastinger.</p>
              <div className="tiles">
                <Fact k="Medlemmer" v={f.members} d="Ikke avsluttet" />
                <Fact k="Aktive" v={f.membersActive} d="Betalende i år" />
                <Fact k="Har koblet appen" v={f.appUsersLinked} d="Logget inn og koblet" />
              </div>
              <p className="note">{f.period_no}</p>
              <div className="tiles">
                <Fact k="Arrangementer" v={f.thisMonth.events} />
                <Fact k="Henvendelser" v={f.thisMonth.requests} />
                <Fact k="Push-varsler sendt" v={f.thisMonth.pushesSent} />
                <Fact k="Janazah kunngjort" v={f.thisMonth.janazahAnnounced} />
              </div>
            </More>
          </div>
        </Card>
      )}
    </>
  );
}
